import { useState } from "react"
import axiosInstance from "../../../../services/axios-client"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"

const usePhaseDeadline = () => {
    const [phases, setPhases] = useState([])
    const [update, setUpdate] = useState(false)
    const [projectName, setProjectName] = useState('')
    const navigate = useNavigate()
    const [isSaving, setIsSaving] = useState(false)

    const onChangeHandler = (e, phaseId) => {
        const updatedData = phases.map(item => {
            if (item.PhaseID === phaseId) {
                if (e.target.type === "checkbox") {
                    return { ...item, [e.target.name]: e.target.checked };
                }
                return { ...item, [e.target.name]: e.target.value };
            }
            return item;
        })
        setPhases(updatedData)
    }

    const postPhase = async () => {
        try {
            setIsSaving(true);
            // Validate that all phases have both StartDate and EndDate filled
            const allPhasesValid = phases.every(phase => phase.StartDate && phase.EndDate);

            if (!allPhasesValid) {
                toast.error("Please fill in both Start Date and Deadline for all phases");
                return;
            }

            let projectId = null;
            let successCount = 0;

            for (const phase of phases) {
                let res = await axiosInstance.post('/auth/project-phases', {
                    ...phase,
                    StartDate: new Date(phase.StartDate).toISOString(),
                    EndDate: new Date(phase.EndDate).toISOString()
                })
                if (res.status === 200) {
                    successCount++;
                    projectId = phase.ProjectID;
                }
            }

            if (successCount > 0) {
                toast.success(`Success Create ${successCount} Phase(s)`);
            }

            if (projectId) {
                navigate(`/project-list/project/${projectId}/1`);
            } else {
                navigate(-1);
            }
        } catch (error) {
            console.error('Error in postPhase:', error);
            if (error.response) {
                console.error('Error response:', error.response);
                toast.error(error?.response?.data?.status?.description || 'Failed to create phase')
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error('An unexpected error occurred')
            }
        } finally {
            setIsSaving(false);
        }
    }

    const putPhase = async () => {
        try {
            setIsSaving(true);
            // Validate that all phases have both StartDate and EndDate filled
            const allPhasesValid = phases.every(phase => phase.StartDate && phase.EndDate);

            if (!allPhasesValid) {
                toast.error("Please fill in both Start Date and Deadline for all phases");
                return;
            }

            let projectId = null;
            let successCount = 0;

            for (const phase of phases) {
                const payload = {
                    ...phase,
                    StartDate: new Date(phase.StartDate).toISOString(),
                    EndDate: new Date(phase.EndDate).toISOString()
                };

                let res = await axiosInstance.put('/auth/project-phases', payload)

                if (res.status === 200) {
                    successCount++;
                    projectId = phase.ProjectID;
                }
            }

            if (successCount > 0) {
                toast.success(`Success Update ${successCount} Phase(s)`);
            }

            if (projectId) {
                navigate(`/project-list/project/${projectId}/1`);
            } else {
                navigate(-1);
            }
        } catch (error) {
            console.error('Error in putPhase:', error);
            console.error('Error code:', error.code);
            console.error('Error message:', error.message);
            console.error('Error config:', error.config);

            if (error.response) {
                console.error('Error response status:', error.response.status);
                console.error('Error response data:', error.response.data);
                toast.error(error?.response?.data?.status?.description || 'Failed to update phase')
            } else if (error.code === 'ECONNABORTED') {
                toast.error('Request timeout - please check your network connection')
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error('An unexpected error occurred')
            }
        } finally {
            setIsSaving(false);
        }
    }

    const getAllPhase = async (projectId) => {
        try {
            let res = await axiosInstance.get('/auth/phases')
            if (res.status === 200) {
                let allPhases = []
                // console.log(res.data.data)
                for (const phase of res.data.data) {
                    allPhases.push({
                        ProjectID: projectId,
                        PhaseID: phase.ID,
                        Name: phase.Name,
                        Order: phase.Order,
                        ReviewSPMO: false,
                        Guideline: false,
                        ShowChart: false,
                        StartDate: '',
                        EndDate: ''
                    })
                }
                setPhases(allPhases)
            }

        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        }
    }

    const getCurrentPhase = async (projectId) => {
        try {
            let res = await axiosInstance.get(`/auth/projects/${projectId}`)
            if (res.status === 200) {
                // console.log(res.data.data)
                setProjectName(res.data.data.Name)
                if (res.data.data.ProjectPhases.length > 0) {
                    let allPhases = []
                    for (const phase of res.data.data.ProjectPhases) {
                        allPhases.push({
                            ID: phase.ID,
                            ProjectID: projectId,
                            PhaseID: phase.PhaseID,
                            Name: phase.Phase.Name,
                            Order: phase.Phase.Order,
                            ReviewSPMO: phase.ReviewSpmo,
                            Guideline: phase.Guideline,
                            ShowChart: phase.ShowChart,
                            StartDate: new Date(phase.StartDate).toISOString().split('T')[0],
                            EndDate: new Date(phase.EndDate).toISOString().split('T')[0]
                        })
                    }
                    setPhases(allPhases)
                    setUpdate(true)
                } else {
                    getAllPhase(projectId)
                    setUpdate(false)
                }
            }

        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        }
    }

    const removePhase = async (phaseId) => {
        setPhases(phases.filter(item => item.PhaseID !== phaseId))
    }

    return {
        phases,
        getAllPhase,
        postPhase,
        removePhase,
        onChangeHandler,
        getCurrentPhase,
        update,
        putPhase,
        isSaving,
        projectName
    }
}

export default usePhaseDeadline